import { RouteWithGates } from '../api/routes';
import { MultiGateComparison, MultiGateGroup } from '../types/MultiGate';

/**
 * One row of the Multi-gate page's gate list.
 *
 * The list is the reference session's own gate first (always on), then every
 * gate that already has a comparison session, then every other starred gate in
 * the team. A gate that has a comparison stays listed even if it has since been
 * unstarred, so it can still be unticked.
 */
export interface GateOption {
  id: string;
  label: string;
  routeDescription: string;
  routeDistance: number | null;
  pinned: boolean;
  isReference: boolean;
  comparison: MultiGateComparison | null;
}

export function gateLabel(
  name: string | null | undefined,
  notes: string | null | undefined,
  routeDistance: number | null | undefined,
): string {
  if (name) return name;
  if (notes) return notes;
  return routeDistance != null ? `${Math.round(routeDistance)} m` : 'gate';
}

export function buildGateOptions(routes: RouteWithGates[], group: MultiGateGroup): GateOption[] {
  const comparisonByGate = new Map<string, MultiGateComparison>();
  group.comparisons.forEach((c) => {
    if (c.gates_id) comparisonByGate.set(c.gates_id, c);
  });

  const options: GateOption[] = [];
  const seen = new Set<string>();
  const add = (option: GateOption) => {
    if (seen.has(option.id)) return;
    seen.add(option.id);
    options.push(option);
  };

  const refGate = group.reference.gate;
  if (refGate) {
    add({
      id: refGate.id,
      label: refGate.label,
      routeDescription: refGate.route_description ?? '',
      routeDistance: refGate.route_distance,
      pinned: refGate.pinned,
      isReference: true,
      comparison: null,
    });
  }

  group.comparisons.forEach((c) => {
    if (!c.gate) return;
    add({
      id: c.gate.id,
      label: c.gate.label,
      routeDescription: c.gate.route_description ?? '',
      routeDistance: c.gate.route_distance,
      pinned: c.gate.pinned,
      isReference: false,
      comparison: c,
    });
  });

  const starred: GateOption[] = [];
  routes.forEach((route) => {
    (route.gates ?? []).forEach((gate) => {
      if (!gate.pinned) return;
      starred.push({
        id: gate.id,
        label: gateLabel(gate.name, gate.notes, gate.route_distance),
        routeDescription: route.description,
        routeDistance: gate.route_distance,
        pinned: true,
        isReference: false,
        comparison: comparisonByGate.get(gate.id) ?? null,
      });
    });
  });
  starred
    .sort((a, b) => a.routeDescription.localeCompare(b.routeDescription))
    .forEach(add);
  return options;
}

/** Gate ids that currently have a comparison session. */
export function comparisonGateIds(group: MultiGateGroup): string[] {
  return group.comparisons.map((c) => c.gates_id).filter((id): id is string => !!id);
}
